import React from 'react';
import { SummaryList } from 'govuk-react-jsx';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import fileDownload from 'js-file-download';

import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { downloadFiles } from '../../firebase';
import downloadFilesFromUrl from '../../utils/downloadFiles';

const ChargepointDetails = ({ claimData }) => {
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  const chargepointsData =
    claimData.grant_type === 'Renters and flat owners'
      ? [claimData.chargepoints_details]
      : claimData.chargepoints_details;

  const isCarParksGrant =
    claimData.grant_type === 'Residential car park' ||
    claimData.grant_type === 'Commercial car park' ||
    claimData.grant_type === 'Staff and fleet car park';

  const chargepointsEvidence = chargepointsData.map((c) => c.evidence);

  const getPhotosNames = (photos) => {
    return photos.map((photo) => {
      if (claimData.grant_type === 'Renters and flat owners') {
        return <section key={photo.image_id}> {photo.image_id}</section>;
      }
      return <section key={photo.image}> {photo.image}</section>;
    });
  };
  const chargePointEvidenceDownload =
    chargepointsEvidence.length > 0
      ? chargepointsEvidence.flatMap((evidenceArray, index) => {
          return evidenceArray.map((evidence) => {
            const imageName =
              evidence?.image_id ||
              evidence?.image?.split('/').pop() ||
              `unknown_image_${index}`;
            return {
              chargePointId: index,
              imageName,
              imagePath: evidence?.image,
            };
          });
        })
      : [];

  const handleClick = async (relevantEvidence) => {
    try {
      const response = await downloadFiles({ download: relevantEvidence });
      if (response.data && Array.isArray(response.data.filesURL)) {
        response.data.filesURL.forEach(async (file) => {
          try {
            const res = await axios.get(file.imageURL, {
              responseType: 'blob',
            });
            fileDownload(res.data, file.imageName);
          } catch (error) {
            console.error(`Failed to download file ${file.imageName}`, error);
          }
        });
      } else {
        console.error('No files available for download.');
      }
    } catch (error) {
      console.error('Failed to download files', error);
    }
  };

  const chargepointRows = chargepointsData.map((chargepointDetails, index) => {
    const relevantEvidence = chargePointEvidenceDownload.filter(
      (item) => item.chargePointId === index
    );
    return {
      key: {
        children: (
          <>
            {copyProvider.getCopy('Installer.Resubmit.s2', 'Chargepoint ')}
            {index + 1}
          </>
        ),
      },
      value: {
        children: (
          <>
            {/* Manufacturer */}
            <strong>
              {copyProvider.getCopy(
                'Installer.Resubmit.ChargepointDetails.s3',
                'Manufacturer: '
              )}
            </strong>
            {chargepointDetails.chargepoint_manufacturer}
            <br />
            {/* Model */}
            <strong>
              {copyProvider.getCopy(
                'Installer.Resubmit.ChargepointDetails.s4',
                'Model: '
              )}
            </strong>
            {chargepointDetails.chargepoint_model}
            <br />
            {/* Chargepoint sockets */}
            <strong>
              {copyProvider.getCopy(
                'Installer.Resubmit.ChargepointDetails.s5',
                'Chargepoint sockets: '
              )}
            </strong>
            {chargepointDetails.number_of_sockets}
            <br />
            {/* Serial number */}
            <strong>
              {copyProvider.getCopy(
                'Installer.Resubmit.ChargepointDetails.s6',
                'Serial number: '
              )}
            </strong>
            {chargepointDetails.serial_number}
            <br />
            {/* Photos */}
            <strong>
              {copyProvider.getCopy(
                'Installer.Resubmit.ChargepointDetails.s7',
                'Photos: '
              )}
            </strong>
            {getPhotosNames(chargepointDetails.evidence)}

            {/* Render a single "Download photos" link */}
            {relevantEvidence.length > 0 && (
              <div>
                <Link to={{}} onClick={() => handleClick(relevantEvidence)}>
                  {copyProvider.getCopy(
                    'Installer.Resubmit.ChargepointDetails.s8',
                    'Download photos'
                  )}
                </Link>
              </div>
            )}
          </>
        ),
        format: 'text',
      },
    };
  });

  const numberOfChargepointsInstalled = () => {
    if (isCarParksGrant) {
      return {
        key: {
          children: copyProvider.getCopy(
            'Installer.Resubmit.ChargepointDetails.s10',
            'Number of chargepoints you installed'
          ),
        },
        value: {
          children: claimData.number_of_chargepoints_installed,
        },
      };
    }
    return '';
  };

  const numberOfChargepointSocketInstalled = () => {
    if (isCarParksGrant) {
      return {
        key: {
          children: copyProvider.getCopy(
            'Installer.Resubmit.ChargepointDetails.s11',
            'Number of chargepoint sockets you installed'
          ),
        },
        value: {
          children: claimData.number_of_chargepoint_sockets_installed,
        },
      };
    }
    return '';
  };

  const carparkPhotos = () => {
    if (isCarParksGrant) {
      const carparkEvidenceDownload =
        claimData.carpark_evidence.length > 0
          ? claimData.carpark_evidence.map((e) => {
              return {
                imageName: e.carpark_id,
                imagePath: e.carpark_link,
              };
            })
          : [];

      const diagrams = claimData.carpark_evidence.map((diagram) => {
        return <section>{diagram.carpark_id}</section>;
      });
      return {
        key: {
          children: copyProvider.getCopy(
            'Installer.Resubmit.InstallationDetails.s13',
            'Car park photos'
          ),
        },
        value: {
          children: [
            <>
              {diagrams}
              <Link
                to={{}}
                onClick={() => downloadFilesFromUrl(carparkEvidenceDownload)}
              >
                {copyProvider.getCopy(
                  'Installer.Resubmit.ChargepointDetails.s12',
                  'Download photos'
                )}
              </Link>
            </>,
          ],
        },
      };
    }
    return '';
  };

  return (
    <>
      {isCarParksGrant ? (
        <h1 className="govuk-heading-m govuk-!-margin-top-5">
          {copyProvider.getCopy(
            'Installer.Resubmit.ChargepointDetails.s9',
            'Chargepoints and car park details'
          )}
        </h1>
      ) : (
        <h1 className="govuk-heading-m govuk-!-margin-top-5">
          {copyProvider.getCopy(
            'Installer.Resubmit.ChargepointDetails.s1',
            'Chargepoint details'
          )}
        </h1>
      )}

      <SummaryList
        className="govuk-heading-m govuk-!-margin-bottom-9"
        rows={[
          numberOfChargepointsInstalled(),
          numberOfChargepointSocketInstalled(),
          ...chargepointRows,
          carparkPhotos(),
        ]}
      />
    </>
  );
};
ChargepointDetails.propTypes = {
  claimData: PropTypes.object.isRequired,
};
export default ChargepointDetails;
